/**
 * Learn → Using Claude (Alex, 4 Nov 2026): what MarketMapr in Claude can
 * do (the homepage's chat demo), prompts to copy grouped by job, and a few
 * habits that get good answers. Opens with "Connect Claude first" (the
 * picture walkthrough) for people who haven't, and the plan note for plans
 * without Claude; the examples stay visible either way.
 */
import React, { useEffect, useState } from 'react';
import ClaudeChatDemo from './ClaudeChatDemo';
import ClaudeConnectGuide from './ClaudeConnectGuide';

interface Prompt { title: string; text: string; does: string }
interface Group { id: string; title: string; prompts: Prompt[] }

// [square brackets] are for the user to fill in.
const GROUPS: Group[] = [
  {
    id: 'find', title: 'Find candidates',
    prompts: [
      {
        title: 'Job spec to shortlist',
        text: 'Here’s a job spec: [paste the spec]. Use MarketMapr to find the best matches in [country / region], split them into sensible groups, and put them all in a new project called “[client] – [role]”. Show me the count before you import, then the five to call first with a line on why each fits.',
        does: 'Reads the spec, counts the market, imports into a project and ranks the first calls.',
      },
      {
        title: 'Read career histories',
        text: 'In my project “[project]”, read the career histories of the people whose current role mentions [keyword]. Tell me who has mostly worked contract roles in the last 5 years, with the evidence for each.',
        does: 'Uses each person’s full role history, dates and employment types. Claude asks which parts to read first, to keep it quick.',
      },
    ],
  },
  {
    id: 'size', title: 'Size a market',
    prompts: [
      {
        title: 'How big, and where',
        text: 'How many [role] are there in [area]? Break it down by city and show me which companies employ the most.',
        does: 'Counts and breakdowns over the whole database. Free and unlimited.',
      },
      {
        title: 'Client-ready market report',
        text: 'Create a market intelligence report on [role] in [country]: where people are based, the top employers, the industry split, and how long people stay in role. Make it something I can send to a client.',
        does: 'Builds the report from MarketMapr’s numbers as of today.',
      },
    ],
  },
  {
    id: 'win', title: 'Win new clients',
    prompts: [
      {
        title: 'Decision-makers at a target account',
        text: 'I want to win [company] as a client for [role] hires. Who should I contact, in what order, and why? Flag anyone new in post.',
        does: 'Maps the leadership and talent team, with start dates and regions.',
      },
      {
        title: 'Hiring managers for live jobs',
        text: 'Show me the new jobs my watches found in “[project]” this week. For each one, find the most likely hiring manager and tell me why.',
        does: 'Reads your job watches, then the people at each hiring company.',
      },
    ],
  },
  {
    id: 'moves', title: 'Movers and backfills',
    prompts: [
      {
        title: 'Who moved, and who has a gap',
        text: 'Show me [role]s in [country] who changed company in the last 2 months. Which companies are hiring, which have lost people, and who should I contact at each?',
        does: 'Finds recent movers, groups them by company joined and left, and names the right contacts.',
      },
      {
        title: 'Backfills in my project',
        text: 'Who has left the companies in my project “[project]” this month? For each move, who should I call about the backfill?',
        does: 'Uses the moves your monitoring has picked up.',
      },
    ],
  },
  {
    id: 'organise', title: 'Organise a project',
    prompts: [
      {
        title: 'Tag a project',
        text: 'Tag the people in my project “[project]” by [seniority / specialism / sector]. Use my existing tags where they fit, and show me 20 examples before you tag the rest.',
        does: 'Learns your tags, shows a sample for you to approve, then tags in batches. Every batch can be undone.',
      },
      {
        title: 'Add people by LinkedIn link',
        text: 'Add these people to my project “[project]”: [paste LinkedIn profile links].',
        does: 'People already in MarketMapr come in free. Claude tells you before it spends any credits.',
      },
    ],
  },
  {
    id: 'daily', title: 'Make it a habit',
    prompts: [
      {
        title: 'Daily call sheet',
        text: 'I work the [market] market in [country]. Every weekday at 7:30am, give me five people to call, why today, and what to say. Use MarketMapr data and news.',
        does: 'Sets up a scheduled task in Claude (on Claude plans that have scheduled tasks).',
      },
    ],
  },
];

const TIPS: string[] = [
  'Name the project you mean (“my project ‘Leeds C#’”) and Claude goes straight to it.',
  'Counts, searches and breakdowns are free and unlimited: ask as many “how many” and “where” questions as you like.',
  'Claude shows you the number before it imports anything, and asks before it spends a credit. Say yes only when it looks right.',
  'On big projects, Claude asks which parts of each profile to read. Recent roles only is quicker and uses less of your Claude allowance.',
  'Ask for the reasoning (“why each one?”). Claude shows its evidence, so you can check it.',
];

const ClaudeLearnTab: React.FC = () => {
  const [state, setState] = useState<{ allowed: boolean; offer: boolean; keys: number; used: boolean } | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let off = false;
    fetch('/api/api-keys', { headers: { Authorization: `Bearer ${window.localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (off || !d) return;
        const keys: any[] = d.keys || [];
        setState({ allowed: !!d.apiAllowed, offer: !!d.connectorOffer, keys: keys.length, used: keys.some((k) => k.last_used_at) });
      })
      .catch(() => {});
    return () => { off = true; };
  }, []);

  const copy = async (id: string, text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 2000); }
    catch { /* the prompt text is selectable */ }
  };

  return (
    <div className="space-y-10">
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold text-gray-900" style={{ textWrap: 'balance' } as React.CSSProperties}>Use MarketMapr from Claude</h2>
        <p className="text-gray-500 mt-1.5 leading-relaxed">
          Ask Claude in plain English and it works on the whole MarketMapr database and your projects: it searches, counts,
          builds projects, finds the decision-makers and writes the report. Here’s what that looks like, and prompts to copy.
        </p>
      </div>

      {/* Where you stand */}
      {state && (
        !state.allowed && !state.offer ? (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
            Claude is part of the Growth and Scale plans. <a href="/settings?tab=subscription" className="underline font-medium">See the plans</a>. The examples below show what it can do.
          </div>
        ) : !state.allowed && state.offer ? (
          <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl text-sm text-violet-900 flex items-center justify-between gap-3 flex-wrap">
            <span>Your plan includes a free month of Claude. Start it in Settings → Integrations, then connect.</span>
            <a href="/settings?tab=integrations" className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700">Start my month</a>
          </div>
        ) : !state.used ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900 flex items-center justify-between gap-3 flex-wrap">
            <span><strong>Connect Claude first.</strong> It takes about 3 minutes, once, and then works in every chat.</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setGuideOpen(true)} className="text-sm font-medium underline">Show me how</button>
              <a href="/settings?tab=integrations" className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Create my key</a>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-900 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
            Claude is connected to your MarketMapr. Copy a prompt below into a new chat.
          </div>
        )
      )}

      {/* What it looks like */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">What it looks like</h3>
        <p className="text-sm text-gray-500 mb-4">Five real ways people use it. Click a tab to jump to one.</p>
        <ClaudeChatDemo />
      </section>

      {/* Prompts */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Prompts to copy</h3>
        <p className="text-sm text-gray-500 mb-5">Copy one into a new Claude chat and fill in the [square brackets].</p>
        <div className="space-y-8">
          {GROUPS.map((g) => (
            <div key={g.id}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{g.title}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.prompts.map((p) => {
                  const id = `${g.id}-${p.title}`;
                  return (
                    <div key={id} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold text-gray-900 text-sm">{p.title}</p>
                        <button onClick={() => copy(id, p.text)}
                          className={`text-xs font-medium px-2.5 py-1 rounded-md border flex-shrink-0 ${copied === id ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                          {copied === id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-gray-50 border border-gray-100 rounded-lg p-3 select-all flex-1">{p.text}</p>
                      <p className="mt-2 text-xs text-gray-500">{p.does}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="max-w-3xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Get the most from it</h3>
        <ul className="space-y-2">
          {TIPS.map((t) => (
            <li key={t} className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
              <span className="text-teal-600 font-bold" aria-hidden>✓</span><span>{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {guideOpen && <ClaudeConnectGuide onClose={() => setGuideOpen(false)} />}
    </div>
  );
};

export default ClaudeLearnTab;
